import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  User,
  Camera,
  Shield,
  Loader2,
  Key,
  KeyRound,
  Smartphone,
  Monitor,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth.context";
import userService from "@/services/user.service";
import authService from "@/services/auth.service";

type UserSessionInfo = Awaited<
  ReturnType<typeof userService.getMySessions>
>["sessions"][number];

const AccountPage: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<UserSessionInfo[]>([]);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [twoFactorDialog, setTwoFactorDialog] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  /** <M-018> O diálogo de "gerar novos códigos de reserva", com o seu próprio campo de senha. */
  const [backupCodesDialog, setBackupCodesDialog] = useState(false);
  const [backupCodesPassword, setBackupCodesPassword] = useState("");
  const [novosCodigosDeReserva, setNovosCodigosDeReserva] = useState<string[]>(
    [],
  );

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    document: "",
    company_document: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [twoFactorForm, setTwoFactorForm] = useState({
    code: "",
    password: "",
  });

  const loadSessions = async () => {
    try {
      const response = await userService.getMySessions();
      setSessions(response.sessions);
    } catch (error) {
      console.error("Erro ao carregar sessões:", error);
    }
  };

  // Carregar dados do perfil
  useEffect(() => {
    if (user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProfile({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        company_name: user.company_name || "",
        document: user.document || "",
        company_document: user.company_document || "",
      });
      // <M-249> Dentro do `if`: sem o `IntegrationGuard`, que segurava a montagem atrás de
      // um esqueleto até o perfil chegar, a página monta na hora e o efeito roda uma vez
      // com `user` nulo — uma ida ao servidor por visita que não servia para nada. Achado
      // de code review.
      loadSessions();
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await userService.updateProfile({
        name: profile.name,
        phone: profile.phone,
        document: profile.document,
        company_name: profile.company_name,
        company_document: profile.company_document,
      });

      await refreshProfile();

      toast({
        title: "Perfil atualizado com sucesso!",
        description: "Suas informações foram atualizadas.",
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar perfil",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      await userService.uploadAvatar(file);
      await refreshProfile();

      toast({
        title: "Avatar atualizado!",
        description: "Sua foto de perfil foi alterada com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao enviar imagem",
        description: "Verifique o tamanho e formato do arquivo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password,
      });

      toast({
        title: "Senha alterada com sucesso!",
        description: "Use sua nova senha no próximo login.",
      });

      setPasswordDialog(false);
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast({
        title: "Erro ao alterar senha",
        description: "Verifique sua senha atual e tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const response = await authService.setup2FA();
      setQrCode(response.qr_code);
      setBackupCodes(response.backup_codes);
      setTwoFactorDialog(true);
    } catch (error) {
      toast({
        title: "Erro ao configurar 2FA",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    try {
      await authService.enable2FA(twoFactorForm.code);
      await refreshProfile();

      toast({
        title: "2FA ativado com sucesso!",
        description: "Sua conta está mais segura agora.",
      });

      setTwoFactorDialog(false);
    } catch (error) {
      toast({
        title: "Código inválido",
        description: "Verifique o código no seu aplicativo autenticador.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * <M-018> Imprime um lote novo de códigos de reserva.
   *
   * É o único caminho para quem ligou o 2FA antes de os códigos passarem a ser gravados —
   * `setup2FA` responde "2FA already enabled" a essa pessoa — e para quem gastou os oito.
   */
  const handleRegenerateBackupCodes = async () => {
    setLoading(true);
    try {
      const resposta = await authService.regenerateBackupCodes(
        backupCodesPassword,
      );
      setNovosCodigosDeReserva(resposta.backup_codes);
      setBackupCodesPassword("");
      await refreshProfile();

      toast({
        title: "Novos códigos de reserva gerados",
        description: "Guarde-os agora: os códigos anteriores deixaram de valer.",
      });
    } catch (error) {
      toast({
        title: "Não foi possível gerar os códigos",
        description: "Confirme a sua senha e tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogoDeCodigosDeReserva = () => {
    setNovosCodigosDeReserva([]);
    setBackupCodesPassword("");
    setBackupCodesDialog(true);
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    try {
      await authService.disable2FA({
        password: twoFactorForm.password,
        code: twoFactorForm.code,
      });
      await refreshProfile();

      toast({
        title: "2FA desativado",
        description: "A autenticação de dois fatores foi removida.",
      });
    } catch (error) {
      toast({
        title: "Erro ao desativar 2FA",
        description: "Verifique sua senha e código.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await userService.revokeSession(sessionId);
      await loadSessions();

      toast({
        title: "Sessão encerrada",
        description: "O dispositivo foi desconectado.",
      });
    } catch (error) {
      toast({
        title: "Erro ao encerrar sessão",
        description: "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {/*
            <M-249> "Meu Perfil", e não "Minha Conta": as duas telas se chamavam a mesma
            coisa, e a outra — usuários da loja, faturas e assinatura — é a que está no
            menu. O seletor de período que ficava aqui saiu junto: esta tela não lê métrica
            nenhuma, e o `handleFilterChange` que ele chamava só fazia `console.log`.
          */}
          <h1 className="text-3xl font-bold tracking-tight">Meu Perfil</h1>
          <p className="text-muted-foreground">
            Gerencie suas informações pessoais e a segurança do seu acesso
          </p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 gap-1">
          <TabsTrigger value="profile" className="text-xs sm:text-sm">
            Perfil
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs sm:text-sm">
            Segurança
          </TabsTrigger>
          <TabsTrigger value="sessions" className="text-xs sm:text-sm">
            Sessões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </CardTitle>
              <CardDescription>
                Atualize suas informações pessoais e foto de perfil
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Photo */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user?.avatar_url ?? undefined} />
                  <AvatarFallback className="text-lg bg-[#2869C1] text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <Label htmlFor="avatar-upload" className="cursor-pointer">
                    <Button variant="outline" className="mb-2" asChild>
                      <span>
                        <Camera className="h-4 w-4 mr-2" />
                        Alterar Foto
                      </span>
                    </Button>
                  </Label>
                  <Input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                  <p className="text-sm text-muted-foreground">
                    JPG, PNG, GIF ou WebP. Máximo 2MB.
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) =>
                      setProfile((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    disabled
                    className="opacity-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) =>
                      setProfile((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="document">CPF</Label>
                  <Input
                    id="document"
                    value={profile.document}
                    onChange={(e) =>
                      setProfile((prev) => ({
                        ...prev,
                        document: e.target.value,
                      }))
                    }
                  />
                </div>
                {user?.user_type === "subscriber" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Nome da Empresa</Label>
                      <Input
                        id="company_name"
                        value={profile.company_name}
                        onChange={(e) =>
                          setProfile((prev) => ({
                            ...prev,
                            company_name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_document">CNPJ</Label>
                      <Input
                        id="company_document"
                        value={profile.company_document}
                        onChange={(e) =>
                          setProfile((prev) => ({
                            ...prev,
                            company_document: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </>
                )}
              </div>

              <Button
                onClick={handleSaveProfile}
                className="bg-[#2869C1] hover:bg-[#2869C1]/90"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar Alterações
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Segurança da Conta
              </CardTitle>
              <CardDescription>
                Gerencie suas configurações de segurança e autenticação
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Key className="h-5 w-5 text-[#2869C1]" />
                    <div>
                      <p className="font-medium">Senha</p>
                      <p className="text-sm text-muted-foreground">
                        Altere sua senha de acesso
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPasswordDialog(true)}
                  >
                    Alterar
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-[#2869C1]" />
                    <div>
                      <p className="font-medium">
                        Autenticação de Dois Fatores (2FA)
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user?.is_2fa_enabled
                          ? "Ativado - Sua conta está protegida"
                          : "Adicione uma camada extra de segurança"}
                      </p>
                    </div>
                  </div>
                  {user?.is_2fa_enabled ? (
                    <Button
                      variant="destructive"
                      onClick={() => setTwoFactorDialog(true)}
                    >
                      Desativar
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={handleSetup2FA}>
                      Configurar
                    </Button>
                  )}
                </div>

                {/*
                  <M-018> Só aparece com o 2FA ligado, porque desligá-lo apaga os códigos.
                  A contagem vem do servidor (`backup_codes_remaining`) e não é derivada de
                  nada na tela: quem perdeu o aplicativo precisa saber se ainda tem caminho
                  de volta, e quantos restam.
                */}
                {user?.is_2fa_enabled && (
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <KeyRound className="h-5 w-5 text-[#2869C1]" />
                      <div>
                        <p className="font-medium">Códigos de Reserva</p>
                        <p className="text-sm text-muted-foreground">
                          {(user?.backup_codes_remaining ?? 0) > 0
                            ? `${user?.backup_codes_remaining} código(s) disponível(is) para entrar sem o aplicativo`
                            : "Nenhum código disponível — sem o aplicativo autenticador você não conseguiria entrar"}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={abrirDialogoDeCodigosDeReserva}
                    >
                      Gerar novos
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Sessões Ativas
              </CardTitle>
              <CardDescription>
                Gerencie os dispositivos conectados à sua conta
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Monitor className="h-5 w-5 text-[#2869C1]" />
                      <div>
                        <p className="font-medium">
                          {session.user_agent?.split("(")[0]?.trim() ||
                            "Dispositivo Desconhecido"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          IP: {session.ip_address} •{" "}
                          {new Date(session.created_at).toLocaleString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleRevokeSession(session.id)}
                    >
                      Encerrar
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Dialogs */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
            <DialogDescription>
              Digite sua senha atual e escolha uma nova senha segura
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Senha Atual</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    current_password: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">Nova Senha</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    new_password: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    confirm_password: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleChangePassword} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Alterar Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        <M-018> Gerar um lote novo de códigos de reserva. Exige só a senha, e não um código
        do aplicativo: quem está aqui pode ter perdido justamente o aparelho que o gera.
      */}
      <Dialog open={backupCodesDialog} onOpenChange={setBackupCodesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Códigos de Reserva</DialogTitle>
            <DialogDescription>
              {novosCodigosDeReserva.length > 0
                ? "Guarde estes códigos em um local seguro. Eles não serão mostrados de novo."
                : "Confirme sua senha para gerar oito códigos novos. Os anteriores deixarão de valer."}
            </DialogDescription>
          </DialogHeader>

          {novosCodigosDeReserva.length > 0 ? (
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 rounded-lg text-xs font-mono">
                {novosCodigosDeReserva.map((code) => (
                  <div key={code}>{code}</div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Cada código entra uma única vez, no lugar do código do
                aplicativo autenticador.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="backup_codes_password">Senha</Label>
              <Input
                id="backup_codes_password"
                type="password"
                value={backupCodesPassword}
                onChange={(e) => setBackupCodesPassword(e.target.value)}
              />
            </div>
          )}

          <DialogFooter>
            {novosCodigosDeReserva.length > 0 ? (
              <Button onClick={() => setBackupCodesDialog(false)}>
                Já guardei
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setBackupCodesDialog(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRegenerateBackupCodes}
                  disabled={loading || backupCodesPassword.length === 0}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Gerar códigos
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={twoFactorDialog} onOpenChange={setTwoFactorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {user?.is_2fa_enabled ? "Desativar 2FA" : "Configurar 2FA"}
            </DialogTitle>
            <DialogDescription>
              {user?.is_2fa_enabled
                ? "Digite sua senha e o código do autenticador — ou um código de reserva — para desativar"
                : "Escaneie o QR Code com seu aplicativo autenticador"}
            </DialogDescription>
          </DialogHeader>

          {!user?.is_2fa_enabled && qrCode && (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="QR Code 2FA"
                  className="w-48 h-48"
                />
              </div>

              <div className="space-y-2">
                <Label>Códigos de Backup</Label>
                <div className="p-3 bg-gray-50 rounded-lg text-xs font-mono">
                  {backupCodes.map((code, index) => (
                    <div key={index}>{code}</div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Guarde estes códigos em um local seguro
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="2fa_code">Código de Verificação</Label>
                <Input
                  id="2fa_code"
                  placeholder="123456"
                  value={twoFactorForm.code}
                  onChange={(e) =>
                    setTwoFactorForm((prev) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          )}

          {user?.is_2fa_enabled && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="2fa_password">Senha</Label>
                <Input
                  id="2fa_password"
                  type="password"
                  value={twoFactorForm.password}
                  onChange={(e) =>
                    setTwoFactorForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="2fa_disable_code">
                  Código 2FA ou código de reserva
                </Label>
                <Input
                  id="2fa_disable_code"
                  placeholder="123456"
                  maxLength={8}
                  value={twoFactorForm.code}
                  onChange={(e) =>
                    setTwoFactorForm((prev) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTwoFactorDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={
                user?.is_2fa_enabled ? handleDisable2FA : handleEnable2FA
              }
              disabled={loading}
              variant={user?.is_2fa_enabled ? "destructive" : "default"}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {user?.is_2fa_enabled ? "Desativar 2FA" : "Ativar 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountPage;
